import {
    appendModalControllerIfRequired,
    makePopoverHandler,
    debounce,
} from '../../global/helpers';
import { Labels } from '../../global/labels';
import {
    addOutsideClickHandler,
    addClickPropagationStopper,
    element,
    addMultipleEventListener,
    getChildrenForSlot,
    keepAllowedElements,
    addContentShiftHandler,
} from '../../global/ui-helpers';
import { UIElement } from '../ui-element';
import { TabIndex } from '../../global/keyboard';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import {
    AllowedTagsList,
    AttributeValue,
    ObservedAttributes,
} from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import {
    NullOr,
    MaybeHTMLElement,
    EventHandlerCallback,
    CallbackWithOptionalDelay,
} from '../../global/utils.type';
import {
    BrowserCallback,
    calculateTooltipOffset,
    Digest,
} from '../../global/index';
import { browser } from '../../global/browser';
import { eventBus } from '../../global/event-bus';
import type { UIAnnotationSlotString } from '../../global/enums';
import styles from './ui-annotation.css';
import { UIModal } from '../modal/ui-modal';
import { UIModalController } from '../modal/ui-modal-controller';
import { Component } from '../../decorators/component.decorator';
import { UIIconColors } from '../icon/ui-icon.helpers';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIAnnotation
 * @element ui-annotation
 * @property {boolean} [expanded] {@attr expanded} Whether an annotation is opened or not. Set to <code>true</code> to open it by default.
 * @property {string} [labelClose] {@attr label-close} Label for close button.
 * @property {HTMLButtonElement} closeButton Shortcut to close button.
 * @classdesc Represents a class for <code>ui-annotation</code> element.
 * @slot {@name title} Title of the annotation.
 * @slot {@name message} Message of the annotation.
 * @slot {@name action} Action element/s of the annotation, maximum of two elements.
 * @example
 * <ui-badge layout="pin">Badge
 *      <ui-annotation expanded>
 *          <p slot="message">It is a long-established fact that a reader will be distracted by the readable content of a page when looking at its layout. The point of using Lorem Ipsum is that it has a more-or-less normal distribution of letters, as opposed to using 'Content here, content here', making it look like readable English.</p>
 *          <ui-chip label="Action" layout="button"></ui-chip>
 *          <ui-chip label="Action" layout="button"></ui-chip>
 *      </ui-annotation>
 *  </ui-badge>
 */
@Component({
    tag: 'ui-annotation',
    styles,
})
export class UIAnnotation extends UIElement {
    /**
     * State: Determines whether annotation can be auto-opened, e.g. on parent focus.
     * Needed to prevent auto-opening again right after it has been closed, so focus remains on parent element.
     * @type {boolean}
     */
    preventAutoFocus: boolean = false;

    /**
     * @type {Array<string>}
     * @readonly
     */
    static readonly allowedHeadingElementTags: AllowedTagsList = [
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
    ] as const;

    /**
     * @type {Array<string>}
     * @readonly
     */
    static readonly allowedMessageElementTags: AllowedTagsList = [
        'p',
        'ul',
        'ol',
    ] as const;

    /**
     * @type {Array<string>}
     * @readonly
     */
    static readonly allowedActionElementTags: AllowedTagsList = [
        'ui-chip',
    ] as const;

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['expanded'];
    }

    /**
     * Define labels what could be localized.
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-annotation', {
            close: 'Close annotation',
        });
    }

    @Attribute(Boolean) expanded?: boolean;
    @Attribute(String) labelClose: TranslationLabels.Label = '';

    @Child('.ui-annotation__close-button') closeButton!: HTMLButtonElement;

    private detachedPopover: HTMLElement | null = null;

    private outsideClickRemover: NullOr<EventHandlerCallback> = null;
    private removeClickPropagationStopper: NullOr<EventHandlerCallback> = null;
    private removeContentShiftHandler: NullOr<EventHandlerCallback> = null;
    private cancelClosePopover: NullOr<EventHandlerCallback> = null;
    private openPopoverHandler: NullOr<EventHandlerCallback> = null;
    private closePopoverHandler: NullOr<
        EventHandlerCallback | CallbackWithOptionalDelay
    > = null;
    private togglePopoverHandler: NullOr<EventHandlerCallback> = null;
    private focusAutoOpenPopoverHandler: NullOr<EventHandlerCallback> = null;
    private hoverCapabilityChangeHandler: NullOr<BrowserCallback> = null;
    private pointerEnterHandler: NullOr<EventHandlerCallback> = null;
    private pointerLeaveHandler: NullOr<EventHandlerCallback> = null;
    private resizeHandler: NullOr<EventHandlerCallback> = null;
    private removeDynamicPopupTriggerHandlers: NullOr<EventHandlerCallback> =
        null;

    /**
     * @type {HTMLElement}
     * @readonly
     */
    get popoverElement(): MaybeHTMLElement {
        if (this.detachedPopover) {
            return this.detachedPopover;
        }
        return this.querySelector('.ui-annotation__popover');
    }

    /**
     * Resolves closing label for screen-readers.
     * @returns {string}
     * @private
     */
    private getAccessibilityCloseLabel(): TranslationLabels.Label {
        return this.labelClose || UIAnnotation.labels.close;
    }

    /**
     * Extracts the elements for the given slot.
     * @param {string} slotName
     * @param {Array<string>} allowedElementTags
     * @returns {Array<HTMLElement>}
     * @private
     */
    private getElements(
        slotName: UIAnnotationSlotString,
        allowedElementTags: AllowedTagsList
    ): Array<HTMLElement> {
        const childrenForSlot = getChildrenForSlot(this, slotName);
        const slotNodes = keepAllowedElements(
            childrenForSlot,
            allowedElementTags
        );

        if (
            UIAnnotation.DEBUG_MODE &&
            childrenForSlot.length !== slotNodes.length
        ) {
            const coloredTags = allowedElementTags
                .map((tag) => `%c${tag}%c`)
                .toString();
            const coloredTagsStyles = allowedElementTags.flatMap(() => [
                'color: red',
                '',
            ]);
            console.warn(
                `Tooltip %c${slotName}%c allows only ${coloredTags} element!`,
                'color: blue',
                '',
                ...coloredTagsStyles
            );
        }
        return slotNodes;
    }

    /**
     * @fires event:modal-open
     * @private
     */
    private openDetachedPopover(): void {
        if (
            this.detachedPopover ||
            !this.parentElement ||
            this.parentElement.offsetParent === null
        ) {
            return;
        }

        const { container, pointer } = calculateTooltipOffset(
            this.parentElement,
            this.popoverElement!
        );

        this.popoverElement?.style.setProperty(
            '--ui-annotation-pointer-horizontal-offset',
            `${pointer.x}px`
        );
        this.popoverElement?.style.setProperty(
            '--ui-annotation-pointer-vertical-offset',
            `${pointer.y}px`
        );
        this.popoverElement?.style.setProperty(
            '--ui-annotation-pointer-angle',
            `${pointer.rotation}deg`
        );
        this.popoverElement?.style.setProperty(
            '--ui-annotation-pointer-hover-area-width',
            `${browser.hoverCapableMediaMatches() ? pointer.hoverAreaWidth : 0}px`
        );
        this.popoverElement?.style.setProperty(
            '--ui-annotation-max-width',
            `${container.width}px`
        );

        const popoverHandler = makePopoverHandler(this.parentElement, {
            popoverHeight: container.height,
            verticalOffset: container.y,
            popoverWidth: container.width,
            horizontalOffset: container.x,
            supportMiddleAlignment: false,
        });
        this.detachedPopover = this.popoverElement;
        this.dispatchCustomEvent('modal-open', {
            type: 'popover',
            content: this.popoverElement,
            params: {
                position: () => {
                    return popoverHandler();
                },
                target: this,
                onOpen: () => {
                    this.popoverElement && this.popoverElement.focus();
                },
                onClose: () => {
                    this.parentElement!.focus();
                    this.expanded = false;
                },
            },
        });
    }

    /**
     * @returns {UIElement | null}
     * @private
     */
    private closeDetachedPopover(): MaybeHTMLElement {
        if (!this.detachedPopover) {
            return null;
        }

        const modal = this.detachedPopover.closest<UIModal>('ui-modal');
        if (modal) {
            this.detachedPopover = null;
            modal.close();
        }

        return modal;
    }

    /**
     * Controls the popover visibility.
     * @private
     */
    private onPopoverStateChange(): void {
        if (!this.popoverElement) {
            return;
        }

        if (this.expanded) {
            this.openDetachedPopover();
            addOutsideClickHandler(
                [this, this.popoverElement, this.parentElement!],
                this.closePopoverHandler as EventHandlerCallback
            ).then((func) => (this.outsideClickRemover = func));
            this.removeClickPropagationStopper = addClickPropagationStopper(
                this.popoverElement,
                'Tooltip click propagation stopped!'
            );
        } else {
            this.closeDetachedPopover();
            this.outsideClickRemover?.();
            this.removeClickPropagationStopper?.();
            this.removeClickPropagationStopper = null;
            this.removeContentShiftHandler?.();
            this.removeContentShiftHandler = null;
        }
    }

    /**
     * Sets popover as expanded.
     * @private
     */
    private onOpenPopover(): void {
        this.expanded = true;
    }

    /**
     * @param {number | undefined} delay
     * @private
     */
    private onClosePopover(delay: number = 0): void {
        if (this.cancelClosePopover) {
            return;
        }

        const closePopover = () => {
            this.cancelClosePopover = null;
            this.expanded = false;
            if (
                !document.activeElement ||
                document.activeElement === document.body
            ) {
                this.parentElement?.focus();
            }
        };

        const debouncedClosePopover = debounce(closePopover.bind(this), delay);
        this.cancelClosePopover = debouncedClosePopover.cancel;
        debouncedClosePopover();
    }

    /**
     * Sets popover as expanded or not.
     * @private
     */
    private onTogglePopover(): void {
        this.expanded = !this.expanded;
    }

    /**
     * @private
     */
    private onPointerEnter(): void {
        if (!this.cancelClosePopover) {
            this.openPopoverHandler!();
            return;
        }

        this.cancelClosePopover();
        this.cancelClosePopover = null;
    }

    /**
     * @private
     */
    private onPointerLeave(): void {
        (this.closePopoverHandler as CallbackWithOptionalDelay)(100);
    }

    /**
     * Adjusts event handlers in the event of device hover capability change.
     * @private
     */
    private onHoverCapabilityChange(): void {
        this.removeDynamicPopupTriggerHandlers?.();
        this.addDeviceDependantEventListeners();
        this.onResize();
    }

    /**
     * @private
     */
    private onResize(): void {
        this.popoverElement?.style.setProperty(
            '--ui-annotation-max-width',
            '360px'
        );
        if (this.detachedPopover) {
            const modal = this.closeDetachedPopover();

            if (modal) {
                const modalUpdateHandler = () => {
                    this.openDetachedPopover();
                    modal?.removeEventListener(
                        'modal-updatelist',
                        modalUpdateHandler
                    );
                };

                modal.addEventListener('modal-updatelist', modalUpdateHandler);
            }
        }
    }

    /**
     * Util for adding device hover capability related event handlers.
     * @private
     */
    private addDeviceDependantEventListeners(): void {
        if (browser.hoverCapableMediaMatches()) {
            const removeOpenPopoverFromParentElementHandler =
                addMultipleEventListener(
                    this.parentElement!,
                    'pointerenter',
                    this.pointerEnterHandler!
                );
            const removeClosePopoverFromParentElementHandler =
                addMultipleEventListener(
                    this.parentElement!,
                    'pointerleave',
                    this.pointerLeaveHandler!
                );
            const removeOpenPopoverFromPopoverElementHandler =
                addMultipleEventListener(
                    this.popoverElement!,
                    'pointerenter',
                    this.pointerEnterHandler!
                );
            const removeClosePopoverFromPopoverElementHandler =
                addMultipleEventListener(
                    this.popoverElement!,
                    'pointerleave',
                    this.pointerLeaveHandler!
                );

            this.removeDynamicPopupTriggerHandlers = () => {
                removeOpenPopoverFromParentElementHandler();
                removeClosePopoverFromParentElementHandler();
                removeOpenPopoverFromPopoverElementHandler();
                removeClosePopoverFromPopoverElementHandler();
            };
            return;
        }

        const removeTogglePopoverFromParentElementHandler =
            addMultipleEventListener(
                this.parentElement!,
                'click',
                this.togglePopoverHandler!
            );
        this.removeDynamicPopupTriggerHandlers = () => {
            removeTogglePopoverFromParentElementHandler();
        };
    }

    /**
     * Util for connecting event handlers.
     * @private
     */
    private addEventListeners(): void {
        this.addDeviceDependantEventListeners();
        browser.addHoverCapableChangeListener(
            this.hoverCapabilityChangeHandler!
        );
        this.closeButton.addEventListener(
            'click',
            this.closePopoverHandler as EventHandlerCallback
        );
        window.addEventListener('resize', this.resizeHandler!);
        this.parentElement?.addEventListener(
            'focus',
            this.focusAutoOpenPopoverHandler!
        );
    }

    /**
     * Util for disconnecting event handlers.
     * @private
     */
    private removeEventListeners(): void {
        this.removeDynamicPopupTriggerHandlers!();
        browser.removeHoverCapableChangeListener(
            this.hoverCapabilityChangeHandler!
        );
        this.closeButton.removeEventListener(
            'click',
            this.closePopoverHandler as EventHandlerCallback
        );
        window.removeEventListener('resize', this.resizeHandler!);
        this.parentElement?.removeEventListener(
            'focus',
            this.focusAutoOpenPopoverHandler!
        );
    }

    /**
     * Automatically open popover on some event, e.g. when parent is focused.
     *  Example use case:
     *  1) when parent gets focus, popover will be toggled to be visible.
     *     if popover is already visible, parent can't be focused as focus is
     *     already on the popover.
     *  2) focus gets trapped inside popover until closed
     *  3) focus returns to parent when popover is closed, popover remains invisible
     *  4) if focus is moved away from parent and back to it, popover auto-opens again.
     * @private
     */
    private onFocusAutoOpenPopover(): void {
        // If autofocus is disabled, enable it again.
        if (this.preventAutoFocus) {
            this.preventAutoFocus = false;
            return;
        }
        // If autofocus is allowed, open popover and disable autofocus
        // so when closed, focus would stay on the parent not open the popover again
        this.openPopoverHandler?.();
        this.preventAutoFocus = true;
    }

    /**
     * Util for waiting for the Roboto font to be loaded and modal controller to have state hydrated and then triggers the annotation.
     * @returns {Promise<void>}
     * @private
     */
    private async renderInitiallyExpanded(): Promise<void> {
        await document.fonts.load('1em RobotoRegular');

        const modalController = document.querySelector<UIModalController>(
            'ui-modal-controller'
        );

        if (modalController?.state !== 'hydrated') {
            const onModalControllerAttached = () => {
                eventBus.removeEventListener(
                    'modal-controller-ready',
                    onModalControllerAttached
                );
                this.onPopoverStateChange();
                this.removeContentShiftHandler = addContentShiftHandler(
                    this,
                    this.onResize.bind(this)
                );
            };

            eventBus.addEventListener(
                'modal-controller-ready',
                onModalControllerAttached
            );
            return;
        }

        this.onPopoverStateChange();
        this.removeContentShiftHandler = addContentShiftHandler(
            this,
            this.onResize.bind(this)
        );
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        if (this.state !== 'hydrated') {
            return;
        }

        switch (name) {
            case 'expanded':
                this.onPopoverStateChange();
                break;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const titleElement = this.getElements(
            'title',
            UIAnnotation.allowedHeadingElementTags
        )[0];
        const messageElements = this.getElements(
            'message',
            UIAnnotation.allowedMessageElementTags
        );
        const actionElements = this.getElements(
            'action',
            UIAnnotation.allowedActionElementTags
        ).slice(0, 2);
        this.innerHTML = '';
        const popoverId = Digest.randomId();

        UIAnnotation.DEBUG_MODE &&
            messageElements.length === 0 &&
            console.warn(
                'Tooltip %cmessage%c is mandatory!',
                'color: blue',
                ''
            );

        this.append(
            element`
                <div
                  id="${popoverId}"
                  class="ui-annotation__popover"
                  tabindex="${TabIndex.Active}"
                >
                    <div class="ui-annotation__content">
                      ${titleElement}
                      ${messageElements.length > 0 ? messageElements : element`<p>Message is mandatory!</p>`}
                      ${actionElements.length > 0 ? element`<div class="ui-annotation__action-buttons-wrapper">${actionElements}</div>` : ''}
                    </div>
                    <div class="ui-annotation__pointer-wrapper">
                      <div class="ui-annotation__pointer"></div>
                    </div>
                    <button
                      class="ui-annotation__close-button -iconed"
                      type="button"
                      title="${this.getAccessibilityCloseLabel()}"
                    >
                        <ui-icon
                          class="-navicon"
                          glyph="redesign-x-mark"
                          color="${UIIconColors.DEFAULT}"
                        ></ui-icon>
                    </button>
                </div>
          `
        );
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        appendModalControllerIfRequired();
        this.openPopoverHandler = this.onOpenPopover.bind(this);
        this.closePopoverHandler = this.onClosePopover.bind(this);
        this.togglePopoverHandler = this.onTogglePopover.bind(this);
        this.focusAutoOpenPopoverHandler =
            this.onFocusAutoOpenPopover.bind(this);
        this.hoverCapabilityChangeHandler =
            this.onHoverCapabilityChange.bind(this);
        this.pointerEnterHandler = this.onPointerEnter.bind(this);
        this.pointerLeaveHandler = this.onPointerLeave.bind(this);
        this.resizeHandler = debounce(this.onResize.bind(this), 200);
        this.addEventListeners();
        if (this.expanded) {
            this.renderInitiallyExpanded();
        }
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.closeDetachedPopover();
        this.removeEventListeners();
        this.outsideClickRemover?.();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.addEventListeners();
    }
}
